import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, MessageSquare, Globe, LayoutGrid, List as ListIcon, Send, Inbox } from "lucide-react";
import { api } from "@/platform/api";

export const STATUSES = [
  { key: "open", label: "Open", color: "#EB5A46" },
  { key: "in_progress", label: "In Progress", color: "#FF9F1A" },
  { key: "ready_for_review", label: "Ready for Review", color: "#00C2E0" },
  { key: "resolved", label: "Resolved", color: "#61BD4F" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

function relTime(ts) {
  const d = new Date(ts).getTime();
  if (!d) return "";
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const initials = (n) => (n || "?").trim()[0]?.toUpperCase() || "?";

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.open;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: `${s.color}22`, color: s.color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} /> {s.label}
    </span>
  );
}

function Avatar({ author }) {
  return (
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold text-[#111]" style={{ background: author?.color || "#7C6BFF" }} title={author?.name}>
      {initials(author?.name)}
    </span>
  );
}

function SourceBadge({ source }) {
  const embed = source === "embed";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[#6E7686]">
      <Globe className="h-3 w-3" /> {embed ? "Live site" : "Demo"}
    </span>
  );
}

function TicketCard({ t, onOpen, draggable, onDragStart }) {
  return (
    <button
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, t)}
      onClick={() => onOpen(t)}
      className="group w-full cursor-pointer rounded-xl border border-[#242A38] bg-[#151A24] p-3.5 text-left transition-all hover:border-[#39415A] hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)] active:scale-[0.99]"
      data-testid={`ticket-card-${t.id}`}
    >
      {t.thumbnail && (
        <div className="mb-2.5 overflow-hidden rounded-lg border border-[#242A38] bg-[#0E1016]">
          <img src={t.thumbnail} alt="Pinned area" loading="lazy" className="h-[104px] w-full object-cover object-top" data-testid={`ticket-thumb-${t.id}`} />
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-[#1C2130] px-1.5 py-0.5 text-[11px] font-bold text-[#98A0B0]">#{t.number}</span>
        <SourceBadge source={t.source} />
        <span className="ml-auto text-[11px] text-[#565E70]">{relTime(t.createdAt)}</span>
      </div>
      <p className="mt-2 line-clamp-3 text-[13.5px] leading-snug text-[#E6E8EE]">{t.message}</p>
      <div className="mt-3 flex items-center gap-2">
        <Avatar author={t.author} />
        <span className="truncate text-[12px] text-[#98A0B0]">{t.author?.name}</span>
        {(t.replies?.length || 0) > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#6E7686]">
            <MessageSquare className="h-3.5 w-3.5" /> {t.replies.length}
          </span>
        )}
      </div>
    </button>
  );
}

function Column({ status, tickets, onOpen, onDropTicket, onDragStart }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/ticket"); if (id) onDropTicket(id, status.key); }}
      className={`flex w-[300px] shrink-0 flex-col rounded-2xl border p-3 transition-colors ${over ? "border-[#6D5EF6] bg-[#171B29]" : "border-[#242A38] bg-[#12151D]"}`}
      data-testid={`board-column-${status.key}`}
    >
      <div className="mb-3 flex items-center gap-2 px-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: status.color }} />
        <span className="text-[13px] font-semibold text-[#E6E8EE]">{status.label}</span>
        <span className="ml-auto rounded-full bg-[#1C2130] px-2 py-0.5 text-[11px] font-medium text-[#98A0B0]">{tickets.length}</span>
      </div>
      <div className="flex min-h-[80px] flex-col gap-2.5">
        {tickets.map((t) => (
          <TicketCard key={t.id} t={t} onOpen={onOpen} draggable onDragStart={onDragStart} />
        ))}
        {tickets.length === 0 && (
          <div className="rounded-xl border border-dashed border-[#242A38] py-8 text-center text-[12px] text-[#565E70]">Drop tickets here</div>
        )}
      </div>
    </div>
  );
}

function TicketDrawer({ ticket, onClose, onStatus, onReply }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.scrollTo(0, bodyRef.current.scrollHeight); }, [ticket?.replies?.length]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!ticket) return null;
  const send = async () => {
    const msg = reply.trim();
    if (!msg) return;
    setSending(true);
    await onReply(ticket.id, msg);
    setReply("");
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-[120]" data-testid="ticket-drawer">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col border-l border-[#242A38] bg-[#0E1016] shadow-2xl animate-in slide-in-from-right duration-300">
        <div className="flex items-center gap-3 border-b border-[#242A38] px-5 py-4">
          <span className="rounded-md bg-[#1C2130] px-2 py-1 text-[12px] font-bold text-[#98A0B0]">#{ticket.number}</span>
          <StatusPill status={ticket.status} />
          <button onClick={onClose} className="ml-auto text-[#6E7686] hover:text-[#F4F5F8]" data-testid="drawer-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="border-b border-[#242A38] px-5 py-4">
          <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#565E70]">Status</span>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => onStatus(ticket.id, s.key)}
                className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-all ${ticket.status === s.key ? "text-[#111]" : "text-[#98A0B0] hover:text-[#E6E8EE]"}`}
                style={ticket.status === s.key ? { background: s.color } : { background: "#1C2130" }}
                data-testid={`drawer-status-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-auto px-5 py-5">
          {ticket.thumbnail && (
            <div className="mb-5 overflow-hidden rounded-xl border border-[#242A38] bg-[#0E1016]">
              <img src={ticket.thumbnail} alt="Pinned area" className="max-h-[260px] w-full object-contain" data-testid="drawer-thumb" />
            </div>
          )}
          <div className="flex items-start gap-3">
            <Avatar author={ticket.author} />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-[#E6E8EE]">{ticket.author?.name} <span className="ml-1 text-[11px] font-normal text-[#565E70]">{relTime(ticket.createdAt)}</span></p>
              <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed text-[#C4C9D4]">{ticket.message}</p>
              {ticket.url && <a href={ticket.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#A99BFF] hover:underline"><Globe className="h-3 w-3" /> {ticket.url}</a>}
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {(ticket.replies || []).map((r) => (
              <div key={r.id} className="flex items-start gap-3">
                <Avatar author={r.author} />
                <div className="min-w-0 flex-1 rounded-xl bg-[#151A24] px-3.5 py-2.5">
                  <p className="text-[12.5px] font-semibold text-[#C4C9D4]">{r.author?.name} <span className="ml-1 text-[11px] font-normal text-[#565E70]">{relTime(r.createdAt)}</span></p>
                  <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[#98A0B0]">{r.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-2 border-t border-[#242A38] px-4 py-3">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            rows={1}
            placeholder="Write a reply…"
            className="max-h-32 flex-1 resize-none rounded-xl border border-[#2A3040] bg-[#151A24] px-3.5 py-2.5 text-[13.5px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]"
            data-testid="drawer-reply-input"
          />
          <button onClick={send} disabled={sending || !reply.trim()} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-white disabled:opacity-40" style={{ backgroundImage: "linear-gradient(135deg,#4F7CFF,#8B5CF6)" }} data-testid="drawer-reply-send">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TicketsBoard({ projectId, domain, initialView = "board", initialTicketId = null }) {
  const [tickets, setTickets] = useState(null);
  const [view, setView] = useState(initialView);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(`/api/projects/${projectId}/tickets`);
    setTickets(res.tickets || []);
  }, [projectId]);

  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  // Deep-link: open the requested ticket drawer once tickets have loaded.
  const [deepOpened, setDeepOpened] = useState(false);
  useEffect(() => {
    if (!deepOpened && initialTicketId && tickets?.some((t) => t.id === initialTicketId)) {
      setActive(initialTicketId);
      setDeepOpened(true);
    }
  }, [initialTicketId, tickets, deepOpened]);

  const activeTicket = useMemo(() => tickets?.find((t) => t.id === active) || null, [tickets, active]);

  const setStatus = async (id, status) => {
    setTickets((list) => list.map((t) => (t.id === id ? { ...t, status } : t)));
    try { await api.patch(`/api/projects/${projectId}/tickets/${id}`, { status }); } catch (_e) { load(); }
  };
  const addReply = async (id, message) => {
    try {
      const updated = await api.post(`/api/projects/${projectId}/tickets/${id}/replies`, { message });
      setTickets((list) => list.map((t) => (t.id === id ? { ...updated, number: t.number } : t)));
    } catch (_e) { load(); }
  };
  const onDragStart = (e, t) => { e.dataTransfer.setData("text/ticket", t.id); e.dataTransfer.effectAllowed = "move"; };

  if (tickets === null) {
    return <div className="grid place-items-center py-20"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>;
  }

  if (tickets.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-14 text-center" data-testid="tickets-empty">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><Inbox className="h-7 w-7" style={{ color: "#A99BFF" }} /></div>
        <p className="mt-4 text-[16px] font-semibold text-[#F4F5F8]">No tickets yet</p>
        <p className="mt-1.5 text-[13.5px] text-[#98A0B0]">Install the Pinlayr widget on {domain || "your site"} and drop a pin — feedback lands here automatically.</p>
      </div>
    );
  }

  const grouped = STATUSES.map((s) => ({ status: s, items: tickets.filter((t) => t.status === s.key) }));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] text-[#98A0B0]">
          <span className="font-semibold text-[#F4F5F8]">{tickets.length}</span> {tickets.length === 1 ? "ticket" : "tickets"}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[#242A38] bg-[#151A24] p-1">
          <button onClick={() => setView("board")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors ${view === "board" ? "bg-[#1C2130] text-[#F4F5F8]" : "text-[#6E7686] hover:text-[#AAB1BF]"}`} data-testid="view-board"><LayoutGrid className="h-4 w-4" /> Board</button>
          <button onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors ${view === "list" ? "bg-[#1C2130] text-[#F4F5F8]" : "text-[#6E7686] hover:text-[#AAB1BF]"}`} data-testid="view-list"><ListIcon className="h-4 w-4" /> List</button>
        </div>
      </div>

      {view === "board" ? (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {grouped.map(({ status, items }) => (
            <Column key={status.key} status={status} tickets={items} onOpen={(t) => setActive(t.id)} onDropTicket={setStatus} onDragStart={onDragStart} />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="tickets-list">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setActive(t.id)} className="flex w-full items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-[#171B29]" data-testid={`list-row-${t.id}`}>
              <span className="w-9 shrink-0 text-[12px] font-bold text-[#565E70]">#{t.number}</span>
              {t.thumbnail ? (
                <img src={t.thumbnail} alt="" loading="lazy" className="h-9 w-14 shrink-0 rounded-md border border-[#242A38] object-cover object-top" />
              ) : null}
              <Avatar author={t.author} />
              <span className="min-w-0 flex-1 truncate text-[13.5px] text-[#E6E8EE]">{t.message}</span>
              {(t.replies?.length || 0) > 0 && <span className="inline-flex items-center gap-1 text-[12px] text-[#6E7686]"><MessageSquare className="h-3.5 w-3.5" /> {t.replies.length}</span>}
              <span className="hidden text-[11px] text-[#565E70] sm:inline">{relTime(t.createdAt)}</span>
              <StatusPill status={t.status} />
            </button>
          ))}
        </div>
      )}

      <TicketDrawer ticket={activeTicket} onClose={() => setActive(null)} onStatus={setStatus} onReply={addReply} />
    </div>
  );
}
